/**
 * @fileoverview Shared single-source-of-truth shell for admin DataGrid pages.
 * @module ui-admin/GridPageShell
 *
 * Responsibilities:
 * - Theme CSS injection, grid templates modal, page header, DataGrid, and
 *   resizable slide-out panel for every grid page.
 * - Auto-save grid layout to localStorage and restore via templates / reset.
 *
 * Dependencies:
 * - DataGrid, ThemeContext, FeatureTierContext, HelpPanel.
 *
 * Architecture:
 * - Presentation-only. Callers pass Dexie-backed row arrays; this shell never
 *   fetches. Layout persistence is browser localStorage (not Dexie).
 *
 * Notes:
 * - Layout is restored on mount via initializePersistedGridState: leftover
 *   autosave unless a pending starred default (Star without Apply) is waiting.
 *   Apply/reset write autosave + colvis + colorder and reconcile contextual overrides together.
 * - "Reset to defaults" restores the page factory layout (not the starred
 *   template) and must clear DataGrid colvis/colorder as well as the shell
 *   autosave key, or remount rehydrates the previous column layout. Reset
 *   also passes null template meta so the Templates button badge clears.
 * - Apply is the Apply button. The star only bookmarks a default; it does not
 *   switch the live grid. Save snapshots live colvis/colorder. Update on a
 *   personal/shared row overwrites that named snapshot with the live layout.
 */

import React, { useState, useRef, useCallback, useEffect, useMemo, useImperativeHandle, forwardRef, type ReactNode } from 'react';
import { X, Maximize2, Pencil, LayoutGrid, RefreshCw, HelpCircle, Compass, type LucideIcon } from 'lucide-react';
import {
  DataGrid,
  persistAppliedGridState,
  cloneGridState,
  resolveMountGridState,
  readContextualColumnVisibilityOverrides,
  writeContextualColumnVisibilityOverrides,
} from '../DataGrid';
import { useAppliedTemplateDrift, loadTemplates as loadStoredTemplates, saveTemplates as persistStoredTemplates } from './useGridPage';
import GridTemplatesModal from './GridTemplatesModal';
import type {
  DataGridColumn,
  DataGridFilter,
  DataGridExpandedRowActions,
  DataGridRowAction,
  DataGridRowDragDrop,
  BulkAction,
  FetchParams,
  FetchResult,
  GridState,
  GridTemplate,
} from '../DataGrid';
import { HelpPanel } from '../HelpPanel/HelpPanel';
import { GuidedTour } from '../GuidedTour/GuidedTour';
import type { GenericHelpSection, TourStep } from '../../help/types';
import { GRID_HELP_SECTIONS } from '../../help/gridHelpContent';
import { GRID_HELP_ILLUSTRATIONS } from '../../help/gridHelpIllustrations';
import { useTheme, themeCSS } from '../../contexts/ThemeContext';
import { useFeatureTier } from '../../contexts/FeatureTierContext';

// ============================================================
// Props
// ============================================================

/**
 * @contract GridPageShellProps
 * Purpose: Configure the shared grid page shell, persistence, templates, and optional detail panel.
 * Fields: Grid metadata, DataGrid pass-through options, factory defaults, and template callbacks.
 * Rules: Contextual column defaults come from the host; explicit local/template choices override them.
 * Sync: Rows are host-provided local-first data; layout preferences remain in browser localStorage.
 */
export interface GridPageShellProps<T> {
  /** Unique grid identifier — used for localStorage keys */
  gridId: string;
  /** Grid type label stored in template documents (e.g. 'inspections', 'lookups') */
  gridType: string;
  /** Page title shown in the header */
  title: string;
  /** Page subtitle shown below the title */
  subtitle?: string;
  /** When true, suppresses the built-in title/subtitle header row */
  hideTitle?: boolean;
  /** When true, suppresses the entire header row (title + buttons) */
  hideHeader?: boolean;

  // --- DataGrid pass-through ---
  columns: DataGridColumn<T>[];
  /** Client-side rows. Optional because server-mode grids supply fetchData instead. */
  data?: T[];
  /** Server-mode fetch — forwarded to DataGrid for paginated/service-backed grids. */
  fetchData?: (params: FetchParams) => Promise<FetchResult<T>>;
  /** Bump to force DataGrid to re-fetch/re-render (server-mode grids). */
  refreshKey?: number | string;
  getRowId: (row: T) => string;
  rowActions?: DataGridRowAction<T>[];
  /** Optional host-controlled native row drag/drop callbacks forwarded to DataGrid. */
  rowDragDrop?: DataGridRowDragDrop<T>;
  filters?: DataGridFilter[];
  bulkActions?: BulkAction<T>[];
  onRowClick?: (row: T) => void;
  activeRowId?: string | null;
  /** Expanded content rendered below its row; receives the shared row-action menu callback. */
  expandedRowId?: string | null;
  expandedRowRender?: (row: T, actions: DataGridExpandedRowActions<T>) => ReactNode;
  searchPlaceholder?: string;
  toolbarActions?: ReactNode;
  toolbarLeftExtra?: ReactNode;
  exportable?: boolean;
  selectable?: boolean;
  defaultPageSize?: number;
  pageSizeOptions?: number[];
  emptyIcon?: LucideIcon;
  emptyTitle?: string;
  emptySubtitle?: string;
  emptyCTA?: { label: string; onClick: () => void };

  /** Extra elements rendered in the page header (e.g. scope selector) */
  headerExtra?: ReactNode;

  /** Content rendered inside the slide-out panel.  When null/undefined the panel is hidden. */
  panelContent?: ReactNode;
  /** Initial width of the slide-out panel in pixels (default 480) */
  panelDefaultWidth?: number;

  // --- Panel header (Layer 2 standard) ---
  /** Title displayed in the default panel header (e.g. entity name) */
  panelTitle?: string;
  /** Subtitle displayed below the panel title */
  panelSubtitle?: string;
  /** Custom panel header — overrides panelTitle/panelSubtitle rendering */
  panelHeader?: ReactNode;
  /** Called when user clicks "Open Full Page" (Maximize2 icon) in panel header */
  onOpenFullPage?: () => void;
  /**
   * When provided, renders a labeled primary action button (default "Edit") in the panel header,
   * next to the title — e.g. to jump straight from the detail slide-in into the full editor.
   */
  onPanelEdit?: () => void;
  /** Label for the panel-header primary action button (defaults to "Edit"). */
  panelEditLabel?: string;
  /** Called when user closes the panel (X button in panel header) */
  onPanelClose?: () => void;

  /** When provided, rendered in place of the DataGrid (header, panel, and shell chrome stay) — e.g. an analytics dashboard view */
  contentOverride?: ReactNode;

  /** Default group-by column IDs — applied on first load before any user interaction or auto-saved state. */
  defaultGroupBy?: string[];
  /** Default sorting — applied on first load before any user interaction or auto-saved state. */
  defaultSorting?: { id: string; desc: boolean }[];

  /** Seed templates specific to this grid (shown under "System Templates") */
  seedTemplates?: GridTemplate[];
  /**
   * Additional localStorage autosave keys to read/clear alongside the standard
   * `datagrid_${gridId}_autosave` key — for grids that historically persisted
   * layout under a legacy key (e.g. Locations' `datagrid_locations_autosave`).
   */
  extraAutosaveKeys?: string[];
  /**
   * Column-visibility fallback merged UNDER the resolved visibility — columns
   * default-hidden unless the user/template explicitly shows them (e.g.
   * Locations' sa_* service-area columns).
   */
  fallbackColumnVisibility?: Record<string, boolean>;
  /** Context-driven column visibility defaults; explicit local/template choices take precedence. */
  contextualColumnDefaults?: Record<string, boolean>;

  /** Called when the user clicks Refresh */
  onRefresh?: () => void;

  /** Called whenever the filtered row count changes — receives the count of currently visible rows */
  onFilteredCountChange?: (count: number) => void;

  /** Authenticated user — used as createdBy on saved grid templates */
  currentUser?: { userId: string; displayName: string };
  /**
   * Optional host FeatureTier flags. ui-admin's FeatureTierContext is a
   * different React context from tenant-web/system-web providers, so shell
   * pages should pass these when the Save-template chrome must follow the host
   * admin/tier. Apply/reset still work without them.
   */
  isAdmin?: boolean;
  /** Optional host feature-tier number (see isAdmin). */
  tier?: number;
  /** Optional override for whether the Save current layout form is shown. */
  canCreateTemplates?: boolean;

  // --- Standard grid help (Layer 2) ---
  /** When false, hides the Help button + panel. Defaults true so every grid exposes help. */
  showHelp?: boolean;
  /** Help sections shown in the panel. Defaults to the standard grid-functionality help. */
  helpSections?: ReadonlyArray<GenericHelpSection>;
  /** Illustration registry for the help sections. Defaults to the standard grid illustrations. */
  helpIllustrations?: Partial<Record<string, React.FC>>;
  /** Heading shown in the help panel content area. */
  helpHeading?: string;
  /** Title shown above the help panel table of contents. */
  helpTocTitle?: string;

  // --- Guided tour (Layer 2) ---
  /** Interactive tour steps. When non-empty, a Tour button appears in the page header. */
  tourSteps?: TourStep[];
  /** Called when the Tour button is clicked, before the tour overlay activates —
   *  lets the page switch view/prep anchors (e.g. tours anchored to a specific tab). */
  onTourStart?: () => void;
  /** Label for the tour button (defaults to "Tour"). */
  tourButtonLabel?: string;
}

// ============================================================
// Imperative handle — lets parent open the templates modal
// ============================================================
export interface GridPageShellHandle {
  openTemplates: () => void;
  openHelp: () => void;
  refresh: () => void;
  templateCount: number;
}

// ============================================================
// localStorage helpers
// ============================================================

const ANONYMOUS_USER = { userId: 'anonymous', displayName: 'Unknown User' };

// ============================================================
// Template persistence
// ============================================================

function loadTemplates(gridId: string, seeds: GridTemplate[]): GridTemplate[] {
  return loadStoredTemplates(gridId, seeds);
}

function saveTemplates(gridId: string, templates: GridTemplate[]) {
  persistStoredTemplates(gridId, templates);
}

// ============================================================
// GridPageShell Component
// ============================================================

function GridPageShellInner<T>(props: GridPageShellProps<T>, ref: React.Ref<GridPageShellHandle>) {
  const {
    gridId, gridType, title, subtitle, hideTitle, hideHeader,
    columns, data, fetchData, refreshKey, getRowId, rowActions, rowDragDrop, filters, bulkActions,
    onRowClick, activeRowId, expandedRowId, expandedRowRender, searchPlaceholder,
    toolbarActions, toolbarLeftExtra, exportable, selectable,
    defaultPageSize = 25, pageSizeOptions,
    emptyIcon, emptyTitle, emptySubtitle, emptyCTA,
    headerExtra, panelContent, panelDefaultWidth = 480,
    panelTitle, panelSubtitle, panelHeader, onOpenFullPage, onPanelEdit, panelEditLabel = 'Edit', onPanelClose,
    contentOverride,
    defaultGroupBy, defaultSorting,
    seedTemplates = [], extraAutosaveKeys = [], fallbackColumnVisibility, contextualColumnDefaults,
    onRefresh, onFilteredCountChange, currentUser,
    isAdmin: isAdminProp, tier: tierProp, canCreateTemplates: canCreateTemplatesProp,
    showHelp = true,
    helpSections = GRID_HELP_SECTIONS,
    helpIllustrations = GRID_HELP_ILLUSTRATIONS,
    helpHeading = 'How to use this grid',
    helpTocTitle = 'Grid help',
    tourSteps,
    onTourStart,
    tourButtonLabel = 'Tour',
  } = props;

  const { theme } = useTheme();
  const { isTier2 } = useFeatureTier();
  const containerRef = useRef<HTMLDivElement>(null);

  // --- Grid state persistence ---
  // Restore leftover autosave, or honor a pending starred default, on mount.
  const [gridState, setGridState] = useState<Partial<GridState>>(() => {
    const resolved = resolveMountGridState({
      gridId,
      templates: loadTemplates(gridId, seedTemplates),
      extraAutosaveKeys,
      defaultPageSize,
      defaultSorting,
      defaultGroupBy,
      fallbackColumnVisibility,
      contextualColumnDefaults,
    });
    // Fallback columns stay hidden unless the resolved state explicitly shows
    // them — a saved layout must not resurrect columns added after it was saved.
    if (fallbackColumnVisibility) {
      resolved.columnVisibility = { ...fallbackColumnVisibility, ...(resolved.columnVisibility ?? {}) };
    }
    return resolved;
  });
  const [gridStateKey, setGridStateKey] = useState(0);
  const { appliedTemplateName, trackStateDrift, markTemplateApplied } = useAppliedTemplateDrift(gridId);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);
  const [showHelpPanel, setShowHelpPanel] = useState(false);
  const [showTour, setShowTour] = useState(false);
  // Keep the help-overlay flag consistent with whether the panel can be shown: if help is
  // disabled while open, the HelpPanel (which owns the close callback) unmounts, so reset the
  // flag here — otherwise the Escape guard below would strand and break Escape-to-close.
  useEffect(() => { if (!showHelp) setShowHelpPanel(false); }, [showHelp]);
  const [templateCount, setTemplateCount] = useState(() => loadTemplates(gridId, seedTemplates).length);
  // Re-derive the badge count when the modal closes — saves, deletes, and
  // updates inside the modal change the persisted template list.
  const templatesModalWasOpen = useRef(showTemplatesModal);
  useEffect(() => {
    if (templatesModalWasOpen.current && !showTemplatesModal) {
      setTemplateCount(loadTemplates(gridId, seedTemplates).length);
    }
    templatesModalWasOpen.current = showTemplatesModal;
  }, [showTemplatesModal, gridId, seedTemplates]);
  const currentGridState = useRef<GridState>(gridState as GridState);

  const themeCssString = useMemo(() => themeCSS(theme), [theme]);

  const handleGridStateChange = useCallback((state: GridState) => {
    currentGridState.current = state;
    persistAppliedGridState(gridId, state, extraAutosaveKeys);
    trackStateDrift(state);
  }, [gridId, trackStateDrift, extraAutosaveKeys]);

  const handleApplyTemplate = useCallback((state: GridState, meta?: { id: string; name: string } | null) => {
    const snapshot = cloneGridState(state);
    if (meta === null) {
      writeContextualColumnVisibilityOverrides(gridId, {});
    } else if (meta?.id) {
      const overrides = readContextualColumnVisibilityOverrides(gridId);
      const namedTemplate = loadTemplates(gridId, seedTemplates).find((template) => template._id === meta.id);
      const namedVisibility = namedTemplate?.state.columnVisibility ?? {};
      for (const id of Object.keys(contextualColumnDefaults ?? {})) {
        if (Object.prototype.hasOwnProperty.call(namedVisibility, id)) overrides[id] = namedVisibility[id];
        else delete overrides[id];
      }
      writeContextualColumnVisibilityOverrides(gridId, overrides);
    }
    persistAppliedGridState(gridId, snapshot, extraAutosaveKeys);
    setGridState(snapshot);
    currentGridState.current = snapshot;
    setGridStateKey((k) => k + 1);
    markTemplateApplied(meta);
  }, [gridId, markTemplateApplied, extraAutosaveKeys, contextualColumnDefaults, seedTemplates]);

  const handleRefresh = useCallback(() => {
    setGridState(currentGridState.current);
    setGridStateKey((k) => k + 1);
    onRefresh?.();
  }, [onRefresh]);

  useImperativeHandle(ref, () => ({
    openTemplates: () => setShowTemplatesModal(true),
    // No-op when help is disabled — the HelpPanel is only mounted when showHelp is true, so
    // setting showHelpPanel without it would strand the flag (and suppress the panel's Escape).
    openHelp: () => { if (showHelp) setShowHelpPanel(true); },
    refresh: handleRefresh,
    templateCount,
  }), [handleRefresh, templateCount, showHelp]);

  // --- Resizable slide-out panel ---
  const [panelWidth, setPanelWidth] = useState(() => {
    try {
      const saved = localStorage.getItem(`${gridId}_panel_width`);
      if (saved) { const n = parseInt(saved, 10); if (n >= 280 && n <= 2000) return n; }
    } catch { /* ignore */ }
    return panelDefaultWidth;
  });
  const isResizing = useRef(false);

  const hasPanelContent = panelContent != null;

  // --- Panel accessibility & responsiveness ---
  // Mobile (<768px): panel takes the full container width.
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia('(max-width: 767px)').matches;
  });
  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const mediaQuery = window.matchMedia('(max-width: 767px)');
    const onChange = (event: MediaQueryListEvent) => setIsMobile(event.matches);
    mediaQuery.addEventListener('change', onChange);
    return () => mediaQuery.removeEventListener('change', onChange);
  }, []);

  // Escape closes the panel; opening moves focus into the panel and closing
  // restores focus to the previously focused element (a11y focus management).
  const panelRef = useRef<HTMLDivElement>(null);
  const lastFocusedBeforeOpen = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!hasPanelContent) return;
    lastFocusedBeforeOpen.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const focusable = panelRef.current?.querySelector<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );
    focusable?.focus();
    return () => {
      lastFocusedBeforeOpen.current?.focus?.();
    };
  }, [hasPanelContent]);

  useEffect(() => {
    if (!hasPanelContent || !onPanelClose) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        // The help overlay sits on top of the slide-out; let it consume Escape first so a
        // single press only dismisses the topmost layer (the help panel), not the panel too.
        // Ordering contract: this handler is on `document` while HelpPanel's is on `window`, and
        // document-phase listeners fire before window-phase ones — so this early return runs
        // first and HelpPanel then closes itself. Keep both targets as-is or this layering breaks.
        if (showHelpPanel || showTour) return;
        onPanelClose();
        return;
      }
      // Focus trap: keep Tab cycling within the panel while it is open.
      if (event.key === 'Tab' && panelRef.current && panelRef.current.contains(document.activeElement)) {
        const focusables = Array.from(panelRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        )).filter((element) => !element.hasAttribute('disabled'));
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [hasPanelContent, onPanelClose, showHelpPanel, showTour]);

  // Icon-only panel-header collapse: below 340px panel width the Edit button
  // drops its label so title + actions fit without overflow (datagrid standard).
  const [panelNarrow, setPanelNarrow] = useState(false);
  useEffect(() => {
    const el = panelRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const width = entries[0]?.contentRect.width ?? 0;
      setPanelNarrow(width > 0 && width < 340);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasPanelContent]);

  const startResize = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isResizing.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const startX = e.clientX;
    const startWidth = panelWidth;
    // Snapshot width ONCE at drag start — offsetWidth grows if panel overflows, so reading
    // it inside onMouseMove produces an ever-growing cap that never stops the panel.
    const fixedContainerWidth = containerRef.current?.offsetWidth || 1200;
    const onMouseMove = (ev: MouseEvent) => {
      if (!isResizing.current) return;
      const delta = startX - ev.clientX;
      setPanelWidth(Math.min(Math.max(280, startWidth + delta), fixedContainerWidth - 215));
    };
    const onMouseUp = () => {
      isResizing.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      setPanelWidth((w) => { try { localStorage.setItem(`${gridId}_panel_width`, String(w)); } catch {} return w; });
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, [panelWidth, gridId]);

  // ============================================================
  // Render
  // ============================================================

  return (
    <>
      <style>{themeCssString}</style>
      <div className="theme-grid flex flex-col overflow-hidden h-full">
        {/* Page Header */}
        {!hideHeader && (
          <div className="shrink-0 px-4 pt-3 pb-2 flex items-center justify-between">
            {!hideTitle && (
              <div>
                <h1 className="text-2xl font-bold">{title}</h1>
                {subtitle && <p className="mt-1">{subtitle}</p>}
              </div>
            )}
            <div className={`flex items-center gap-2${hideTitle ? ' ml-auto' : ''}`}>
              {headerExtra}
              {showHelp && (
                <button onClick={() => setShowHelpPanel(true)}
                  data-testid="grid-help-button"
                  title="How to use this grid"
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors">
                  <HelpCircle className="h-4 w-4" /> Help
                </button>
              )}
              {tourSteps && tourSteps.length > 0 && (
                <button onClick={() => { onTourStart?.(); setShowTour(true); }}
                  data-testid="grid-tour-button"
                  title="Take a guided tour"
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors">
                  <Compass className="h-4 w-4" /> {tourButtonLabel}
                </button>
              )}
              <button onClick={() => setShowTemplatesModal(true)}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors">
                <LayoutGrid className="h-4 w-4" /> Templates
                {appliedTemplateName && (
                  <span className="ml-0.5 max-w-[140px] truncate text-xs font-semibold text-orange-700" title={appliedTemplateName}>
                    {appliedTemplateName}
                  </span>
                )}
                {templateCount > 0 && (
                  <span className="ml-0.5 inline-flex items-center justify-center h-5 min-w-[20px] px-1 rounded-full bg-orange-500 text-white text-xs font-bold">
                    {templateCount}
                  </span>
                )}
              </button>
              {onRefresh && (
                <button onClick={handleRefresh}
                  className="inline-flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors">
                  <RefreshCw className="h-4 w-4" /> Refresh
                </button>
              )}
            </div>
          </div>
        )}

        {/* Grid + slide-out */}
        <div className="flex-1 min-h-0 flex overflow-hidden" ref={containerRef}>
          {/* DataGrid (or caller-provided override content, e.g. an analytics view) — on
              mobile the open panel takes the full row, so the grid is hidden to avoid
              the 200px minimum squeezing the panel inside the overflow-hidden parent */}
          <div className="flex-1 min-w-0 px-2 pb-1.5" style={{ minWidth: 200, display: isMobile && hasPanelContent ? 'none' : undefined }}>
            {contentOverride ?? (
            <DataGrid<T>
              key={gridStateKey}
              gridId={gridId}
              columns={columns}
              data={data}
              fetchData={fetchData}
              refreshKey={refreshKey}
              getRowId={getRowId}
              rowActions={rowActions}
              rowDragDrop={rowDragDrop}
              filters={filters}
              bulkActions={bulkActions}
              onRowClick={onRowClick}
              activeRowId={activeRowId}
              expandedRowId={expandedRowId}
              expandedRowRender={expandedRowRender}
              searchPlaceholder={searchPlaceholder}
              toolbarActions={toolbarActions}
              toolbarLeftExtra={toolbarLeftExtra}
              exportable={exportable}
              selectable={selectable}
              defaultPageSize={defaultPageSize}
              pageSizeOptions={pageSizeOptions}
              emptyIcon={emptyIcon}
              emptyTitle={emptyTitle}
              emptySubtitle={emptySubtitle}
              emptyCTA={emptyCTA}
              containerClassName="h-full"
              initialState={gridState}
              contextualColumnDefaults={contextualColumnDefaults}
              onStateChange={handleGridStateChange}
              onFilteredCountChange={onFilteredCountChange}
              isTier2={isTier2}
            />
            )}
          </div>

          {/* Slide-out panel with draggable resizer — animated open/close */}
          <div
            ref={panelRef}
            role="complementary"
            aria-label={panelTitle ?? 'Detail panel'}
            className="shrink-0 flex flex-row border-l border-gray-200 overflow-hidden"
            style={{
              width: hasPanelContent
                ? (isMobile ? '100%' : Math.min(panelWidth, (containerRef.current?.offsetWidth ?? 9999) - 215))
                : 0,
              opacity: hasPanelContent ? 1 : 0,
              transition: isResizing.current
                ? 'none'
                : 'width 300ms cubic-bezier(0.4, 0, 0.2, 1), opacity 200ms ease-in-out',
              maxWidth: isMobile ? '100%' : 'calc(100% - 200px)',
            }}
          >
            {/* Draggable resizer handle */}
            <div className="shrink-0 flex items-center justify-center cursor-col-resize group" style={{ width: 6 }}
              onMouseDown={startResize}>
              <div className="h-full transition-colors duration-150"
                style={{ width: 3, borderRadius: 2, background: theme.cardBorder }}
                onMouseEnter={(e) => { e.currentTarget.style.background = theme.accent; e.currentTarget.style.width = '5px'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = theme.cardBorder; e.currentTarget.style.width = '3px'; }}
              />
            </div>
            {/* Panel header + content */}
            <div className="flex-1 min-w-0 flex flex-col" style={{ background: theme.cardBg }}>
              {/* Default panel header (Layer 2) — shown when panelTitle or panelHeader is provided */}
              {hasPanelContent && (panelHeader || panelTitle) && (
                <div className="shrink-0 flex items-center justify-between px-4 py-3 border-b border-gray-200">
                  {panelHeader || (
                    <div className="flex-1 min-w-0 mr-2">
                      <h2 className="text-base font-semibold truncate" style={{ color: theme.textPrimary }}>{panelTitle}</h2>
                      {panelSubtitle && <p className="text-xs mt-0.5" style={{ color: theme.textSecondary }}>{panelSubtitle}</p>}
                    </div>
                  )}
                  {!panelHeader && (
                    <div className="flex items-center gap-1.5 shrink-0">
                      {onPanelEdit && (
                        <button onClick={onPanelEdit}
                          data-testid="panel-edit-button"
                          aria-label={panelEditLabel}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-orange-600 text-white hover:bg-orange-700 transition-colors"
                          title={panelEditLabel}>
                          <Pencil className="h-4 w-4" /> {!panelNarrow && panelEditLabel}
                        </button>
                      )}
                      {onOpenFullPage && (
                        <button onClick={onOpenFullPage}
                          aria-label="Open full page"
                          className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
                          title="Open full page"
                          style={{ color: theme.textSecondary }}>
                          <Maximize2 className="h-4 w-4" />
                        </button>
                      )}
                      {onPanelClose && (
                        <button onClick={onPanelClose}
                          data-testid="slide-in-close"
                          aria-label="Close panel"
                          className="p-1.5 rounded-md hover:bg-gray-100 transition-colors"
                          title="Close panel"
                          style={{ color: theme.textSecondary }}>
                          <X className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}
              <div className="flex-1 min-h-0 overflow-hidden">
                {panelContent}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Grid Templates Modal */}
      {showTemplatesModal && (
        <GridTemplatesModal
          gridId={gridId}
          gridType={gridType}
          seedTemplates={seedTemplates}
          currentState={currentGridState.current}
          onApply={handleApplyTemplate}
          onClose={() => setShowTemplatesModal(false)}
          currentUser={currentUser ?? ANONYMOUS_USER}
          defaultPageSize={defaultPageSize}
          defaultSorting={defaultSorting}
          defaultGroupBy={defaultGroupBy}
          isAdmin={isAdminProp}
          tier={tierProp}
          canCreateTemplates={canCreateTemplatesProp}
          knownColumnIds={columns.map((c) => c.id).filter((id) => id !== '_actions' && id !== '_select')}
          extraAutosaveKeys={extraAutosaveKeys}
          fallbackColumnVisibility={fallbackColumnVisibility}
        />
      )}

      {/* Standard grid help panel */}
      {showHelp && (
        <HelpPanel<string, string>
          open={showHelpPanel}
          onClose={() => setShowHelpPanel(false)}
          onStartTour={tourSteps && tourSteps.length > 0
            ? () => { setShowHelpPanel(false); onTourStart?.(); setShowTour(true); }
            : undefined}
          sections={helpSections}
          illustrations={helpIllustrations}
          heading={helpHeading}
          tocTitle={helpTocTitle}
          ariaLabel="Grid help"
          panelTestId="grid-help-panel"
        />
      )}

      {/* Guided tour overlay — driven by the Tour header button */}
      {tourSteps && tourSteps.length > 0 && (
        <GuidedTour steps={tourSteps} active={showTour} onComplete={() => setShowTour(false)} />
      )}
    </>
  );
}

// forwardRef doesn't play well with generics in TS — cast as the generic version
const GridPageShell = forwardRef(GridPageShellInner) as <T>(
  props: GridPageShellProps<T> & { ref?: React.Ref<GridPageShellHandle> }
) => React.ReactElement;

export default GridPageShell;
